import { getConfig, postCheck } from '../../core/rest';
import { clear, el } from '../../core/dom';
import { makeBanner, makeChip, makeFieldHead, makePanel, setChip } from '../../core/shell';

type CheckRow = {
	id: string;
	header: string;
	state: string;
	evidence: string;
	explanation: string;
	fix: string;
};

type HeadersResponse = {
	status: string;
	checked_at: string;
	target: { display_host: string; final_url: string; http_status: number };
	checks: CheckRow[];
	limitations: string[];
};

function idlePanels(panels: HTMLElement): void {
	clear(panels);
	panels.appendChild(
		makePanel(
			'Header observations',
			el('p', { className: 'kb-tools__muted' }, [
				'Enter a public URL to see which common security headers were present on the response. Missing is not the same as unsafe.',
			])
		)
	);
}

function renderChecks(panels: HTMLElement, data: HeadersResponse): void {
	clear(panels);
	const meta = el('dl', { className: 'kb-tools__claim-list' });
	const rows: Array<[string, string]> = [
		['Host', data.target.display_host],
		['Final URL', data.target.final_url],
		['HTTP status', String(data.target.http_status)],
		['Checked at', data.checked_at],
	];
	for (const [k, v] of rows) {
		meta.appendChild(el('dt', {}, [k]));
		meta.appendChild(el('dd', {}, [v]));
	}
	panels.appendChild(makePanel('Target', meta));

	const table = el('div', { className: 'kb-tools__table' });
	for (const row of data.checks) {
		const item = el('article', { className: 'kb-tools__table-row kb-tools__panel--result' });
		const stateLabel = row.state === 'observed' ? 'Observed' : 'Not observed';
		const stateTone = row.state === 'observed' ? 'ok' : 'warn';
		item.appendChild(
			el('div', { className: 'kb-tools__panel-head' }, [
				el('h3', { className: 'kb-tools__panel-title' }, [row.header]),
				el('span', { className: `kb-tools__chip kb-tools__chip--${stateTone}` }, [stateLabel]),
			])
		);
		if (row.evidence) {
			const pre = el('pre', { className: 'kb-tools-json kb-tools-json--compact' });
			pre.textContent = row.evidence;
			item.appendChild(pre);
		}
		item.appendChild(el('p', { className: 'kb-tools__muted' }, [row.explanation]));
		if (row.fix && row.state !== 'observed') {
			const fix = el('pre', { className: 'kb-tools-json kb-tools-json--compact' });
			fix.textContent = 'Example: ' + row.fix;
			item.appendChild(fix);
		}
		table.appendChild(item);
	}
	panels.appendChild(makePanel('Checks', table));
}

export function mountSecurityHeaders(root: HTMLElement): void {
	root.classList.add('kb-tools', 'kb-tools--headers');
	root.querySelectorAll('.kb-tools__ssr-fallback').forEach((n) => n.remove());

	const input = el('input', {
		className: 'kb-tools__input',
		id: 'kb-headers-url',
		type: 'text',
		autocomplete: 'off',
		spellcheck: 'false',
		placeholder: 'https://example.org',
		'aria-describedby': 'kb-headers-status kb-headers-privacy',
	}) as HTMLInputElement;

	const btn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--primary' }, [
		'Check headers',
	]) as HTMLButtonElement;
	const sample = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Use example.org',
	]) as HTMLButtonElement;
	const clearBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Clear',
	]) as HTMLButtonElement;

	const compose = el('div', { className: 'kb-tools__compose' }, [
		makeFieldHead(
			el('label', { className: 'kb-tools__label', for: 'kb-headers-url' }, ['URL or domain']),
			'Enter to check'
		),
		input,
		el('div', { className: 'kb-tools__actions' }, [btn, sample, clearBtn]),
	]);

	const chip = makeChip('kb-headers-status');
	const warnings = el('ul', { className: 'kb-tools__warnings' });
	const panels = el('div', { className: 'kb-tools__panels kb-tools__panels--stack' });
	const results = el('div', { className: 'kb-tools__results' }, [
		el('div', { className: 'kb-tools__status-row' }, [chip]),
		warnings,
		panels,
	]);

	clear(root);
	root.append(
		makeBanner(
			'Remote',
			'A restricted public request will be sent for the normalized URL only — never cookies or credentials.'
		),
		el('div', { className: 'kb-tools__workspace kb-tools__workspace--remote' }, [compose, results]),
		el('p', { className: 'kb-tools__privacy', id: 'kb-headers-privacy' }, [
			'“Not observed” is not the same as “unsafe”. No numeric score is shown.',
		])
	);

	const cfg = getConfig();
	if (!cfg.remoteEnabled) {
		setChip(chip, 'Remote checks disabled', 'warn');
	} else {
		setChip(chip, 'Waiting for a URL', 'idle');
	}
	idlePanels(panels);

	const run = async (): Promise<void> => {
		clear(warnings);
		setChip(chip, 'Checking…', 'idle');
		btn.disabled = true;
		try {
			const data = await postCheck<HeadersResponse>('check/security-headers', {
				url: input.value.trim(),
			});
			setChip(
				chip,
				`Completed · ${data.target.display_host} · HTTP ${data.target.http_status}`,
				'ok'
			);
			for (const lim of data.limitations || []) {
				warnings.appendChild(el('li', {}, [lim]));
			}
			renderChecks(panels, data);
		} catch (err) {
			setChip(chip, err instanceof Error ? err.message : 'Could not check', 'error');
			idlePanels(panels);
		} finally {
			btn.disabled = false;
		}
	};

	btn.addEventListener('click', () => {
		void run();
	});
	sample.addEventListener('click', () => {
		input.value = 'https://example.org';
		input.focus();
	});
	clearBtn.addEventListener('click', () => {
		input.value = '';
		clear(warnings);
		setChip(chip, cfg.remoteEnabled ? 'Waiting for a URL' : 'Remote checks disabled', cfg.remoteEnabled ? 'idle' : 'warn');
		idlePanels(panels);
		input.focus();
	});
	input.addEventListener('keydown', (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			void run();
		}
	});
}
